import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset, resetPassword } from '../core/services/playerAuthClient';
import Alert from '../shared/components/ui/Alert';

function checkPassword(pw) {
  const errors = [];
  if (pw.length < 8)            errors.push('Mindst 8 tegn');
  if (!/[A-Z]/.test(pw))        errors.push('Stort bogstav');
  if (!/[a-z]/.test(pw))        errors.push('Lille bogstav');
  if (!/[0-9]/.test(pw))        errors.push('Et tal');
  if (!/[^A-Za-z0-9]/.test(pw)) errors.push('Specialtegn');
  return errors;
}

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // If token + email are in URL, we're on the "set new password" step
  const token = searchParams.get('token');
  const emailFromUrl = searchParams.get('email') || '';

  // Request step
  const [email, setEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  // Admin reset request step
  const [adminMemberNumber, setAdminMemberNumber] = useState('');
  const [adminRequestSent, setAdminRequestSent] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);

  // Reset step
  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [pwErrors, setPwErrors]     = useState([]);

  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  // ─── Request reset ────────────────────────────────────────────────────────────
  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) { setError(t('resetPassword.error.emailRequired')); return; }
    if (!emailRegex.test(email)) { setError(t('resetPassword.error.invalidEmail')); return; }

    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setRequestSent(true);
    } catch {
      // Always show generic success to prevent email enumeration
      setRequestSent(true);
    } finally {
      setLoading(false);
    }
  };

  // ─── Apply reset ──────────────────────────────────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    const pwErrs = checkPassword(password);
    if (pwErrs.length > 0) { setPwErrors(pwErrs); return; }
    if (password !== confirm) { setError(t('resetPassword.error.passwordMismatch')); return; }

    setLoading(true);
    try {
      const { error: resetErr } = await resetPassword(password, token);
      if (resetErr) {
        setError(t('resetPassword.error.invalidToken'));
        return;
      }
      setSuccess(t('resetPassword.success.passwordReset'));
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch {
      setError(t('resetPassword.error.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdminRequest = async (e) => {
    e.preventDefault();
    if (!adminMemberNumber.trim()) return;
    setAdminLoading(true);
    try {
      await fetch('/api/player-auth-ext/request-admin-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberNumber: adminMemberNumber.trim() }),
      });
    } finally {
      setAdminLoading(false);
      setAdminRequestSent(true);
    }
  };

  const inputClass = 'mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
          {t('resetPassword.heading.title')}
        </h1>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">

          {error && <Alert variant="error" className="mb-4 text-sm">{error}</Alert>}
          {success && <Alert variant="success" className="mb-4 text-sm">{success}</Alert>}

          {/* ── Apply new password (token from email link) ─────────────────── */}
          {token ? (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 text-center mb-5">
                {t('resetPassword.heading.newPasswordTitle')}
              </h2>
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    {t('resetPassword.input.label.password')}
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setPwErrors(checkPassword(e.target.value)); }}
                    className={inputClass}
                    placeholder={t('resetPassword.input.placeholder.password')}
                    required
                    disabled={loading}
                  />
                  {password && (
                    <ul className="mt-1 space-y-0.5">
                      {['Mindst 8 tegn', 'Stort bogstav', 'Lille bogstav', 'Et tal', 'Specialtegn'].map((req) => (
                        <li key={req} className={`text-xs flex items-center gap-1 ${pwErrors.includes(req) ? 'text-red-500' : 'text-green-600'}`}>
                          {pwErrors.includes(req) ? '✗' : '✓'} {req}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <label htmlFor="confirm" className="block text-sm font-medium text-gray-700">
                    {t('resetPassword.input.label.confirmPassword')}
                  </label>
                  <input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className={`${inputClass} ${confirm && confirm !== password ? 'border-red-300' : ''}`}
                    placeholder={t('resetPassword.input.placeholder.confirmPassword')}
                    required
                    disabled={loading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || pwErrors.length > 0}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? t('resetPassword.button.setting') : t('resetPassword.button.setPassword')}
                </button>
              </form>
            </div>
          ) : requestSent ? (
            /* ── Email sent confirmation ─────────────────────────────────── */
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-gray-700 font-medium mb-1">{t('resetPassword.text.emailSent')}</p>
              <p className="text-sm text-gray-500 mb-6">{t('resetPassword.text.checkSpam')}</p>
              <Link to="/login" className="text-indigo-600 hover:text-indigo-500 text-sm font-medium">
                {t('resetPassword.button.backToLogin')}
              </Link>
            </div>
          ) : (
            /* ── Request reset ───────────────────────────────────────────── */
            <div>
              <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
                {t('resetPassword.heading.requestTitle')}
              </h2>
              <p className="text-sm text-gray-500 text-center mb-5">
                {t('resetPassword.text.requestInfo')}
              </p>
              <form onSubmit={handleRequest} className="space-y-4">
                <div>
                  <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700">
                    {t('resetPassword.input.label.email')}
                  </label>
                  <input
                    id="resetEmail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder={t('resetPassword.input.placeholder.email')}
                    required
                    disabled={loading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? t('resetPassword.button.sending') : t('resetPassword.button.sendLink')}
                </button>
              </form>
              <div className="mt-4 text-center">
                <Link to="/login" className="text-sm text-indigo-600 hover:text-indigo-500">
                  {t('resetPassword.button.backToLogin')}
                </Link>
              </div>

              {/* Admin reset request — for players without email */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                  {t('resetPassword.adminReset.heading')}
                </h3>
                {adminRequestSent ? (
                  <p className="text-sm text-green-600">{t('resetPassword.adminReset.text.sent')}</p>
                ) : (
                  <form onSubmit={handleAdminRequest} className="space-y-3 mt-3">
                    <div>
                      <label htmlFor="adminMn" className="block text-sm font-medium text-gray-700">
                        {t('resetPassword.adminReset.input.label.memberNumber')}
                      </label>
                      <input
                        id="adminMn"
                        type="text"
                        value={adminMemberNumber}
                        onChange={(e) => setAdminMemberNumber(e.target.value)}
                        className={inputClass}
                        placeholder={t('resetPassword.adminReset.input.placeholder.memberNumber')}
                        disabled={adminLoading}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={adminLoading || !adminMemberNumber.trim()}
                      className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {adminLoading ? t('resetPassword.adminReset.button.requesting') : t('resetPassword.adminReset.button.request')}
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
